import { memo } from "react";
import TablePagination from "@mui/material/TablePagination";
import {
  checkboxColor,
  checkboxFocusRingColor,
  cloudyIndigoColor,
  fadeBlueWhite,
  slateBorderHoverColor,
  slatePaleBg,
  slateTextColor,
  softBlueBorderColor,
  softBlueLineColor,
  softBlueSelectedBg,
  softBlueSelectedHover,
  textMutedSubtitleColor,
  textPrimaryMenuShadowColor,
  white,
  whiteShadeColor,
  whiteStrongShadeColor,
} from "../../config/ColorObj";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../../utils/useClientPagination";

const paginationSx = {
  overflow: "hidden",
  border: `1px solid ${whiteStrongShadeColor}`,
  borderRadius: "10px",
  backgroundColor: whiteShadeColor,
  color: textMutedSubtitleColor,
  fontFamily: "inherit",
  "& .MuiTablePagination-toolbar": {
    minHeight: 56,
    padding: "8px 14px",
    gap: "10px",
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.2,
  },
  "& .MuiTablePagination-selectLabel": {
    fontWeight: 500,
    color: cloudyIndigoColor,
  },
  "& .MuiTablePagination-input": {
    margin: "0 4px 0 0",
    minWidth: 76,
    border: `1px solid ${softBlueBorderColor}`,
    borderRadius: "8px",
    backgroundColor: white,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: checkboxColor,
    },
    "&.Mui-focused": {
      borderColor: checkboxColor,
      boxShadow: `0 0 0 3px ${checkboxFocusRingColor}`,
    },
  },
  "& .MuiTablePagination-select": {
    padding: "6px 28px 6px 12px !important",
    fontSize: 13,
    fontWeight: 600,
    color: slateTextColor,
    textAlign: "left",
    textAlignLast: "left",
  },
  "& .MuiTablePagination-selectIcon": {
    right: 6,
    color: checkboxColor,
  },
  "& .MuiTablePagination-displayedRows": {
    minWidth: 96,
    padding: "6px 12px",
    border: `1px solid ${softBlueLineColor}`,
    borderRadius: "999px",
    backgroundColor: white,
    fontWeight: 600,
    color: slateTextColor,
    textAlign: "center",
  },
  "& .MuiTablePagination-actions": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginLeft: "4px",
  },
  "& .MuiTablePagination-actions .MuiIconButton-root": {
    width: 32,
    height: 32,
    margin: 0,
    padding: "4px",
    border: `1px solid ${softBlueBorderColor}`,
    borderRadius: "8px",
    backgroundColor: white,
    color: checkboxColor,
    transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: checkboxColor,
      borderColor: checkboxColor,
      color: white,
    },
    "&.Mui-disabled": {
      backgroundColor: slatePaleBg,
      borderColor: fadeBlueWhite,
      color: slateBorderHoverColor,
    },
  },
  "@media (max-width: 640px)": {
    "& .MuiTablePagination-toolbar": {
      flexWrap: "wrap",
      justifyContent: "center",
      padding: "10px 12px",
    },
    "& .MuiTablePagination-spacer": {
      display: "none",
    },
    "& .MuiTablePagination-displayedRows": {
      minWidth: 0,
    },
  },
};

const attachedPaginationSx = {
  ...paginationSx,
  border: "none",
  borderTop: `1px solid ${softBlueLineColor}`,
  borderRadius: 0,
  backgroundColor: whiteShadeColor,
};

const menuPaperSx = {
  mt: "6px",
  borderRadius: "10px",
  boxShadow: `0 10px 28px ${textPrimaryMenuShadowColor}`,
  "& .MuiMenuItem-root": {
    minHeight: 36,
    fontSize: 13,
    fontWeight: 600,
  },
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor: softBlueSelectedBg,
    color: checkboxColor,
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    backgroundColor: softBlueSelectedHover,
  },
};

const TablePaginationCompo = memo(function TablePaginationCompo({
  count = 0,
  page = 0,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  className = "",
  attached = false,
  hideWhenEmpty = true,
}) {
  if (hideWhenEmpty && count <= 0) {
    return null;
  }

  return (
    <TablePagination
      className={className}
      sx={attached ? attachedPaginationSx : paginationSx}
      component="div"
      count={count}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={rowsPerPageOptions}
      labelRowsPerPage="Rows per page"
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: menuPaperSx,
          },
        },
      }}
    />
  );
});

export default TablePaginationCompo;
